import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from 'react-router-dom';
import ApiCall from '../../utils/Api';
import { CircularProgress, Typography } from '@mui/material';

// No sample data - only backend data

export default function DataTransactionTable({ reloadKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatEth = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    
    // If the value is extremely large (likely in wei), convert from wei to ETH
    if (n > 1000000) {
      const ethValue = n / 1e18; // Convert wei to ETH
      return `${ethValue.toFixed(6)} ETH`;
    }
    
    // If it's already in ETH, format to 6 decimal places
    return `${n.toFixed(6)} ETH`;
  };
  const formatUsd = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return `${n.toFixed(2)} USD`;
  };

  // Fetch data transactions from backend
  useEffect(() => {
    const fetchDataTransactions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Current user context
        const currentProviderName = localStorage.getItem('name');
        const currentRole = localStorage.getItem('role');
        const currentId = localStorage.getItem('id');
        
        let collected = [];

        // 1) Admin: get all transactions
        if (currentRole === 'admin') {
          const resp = await ApiCall.SECURE_API.get(`/admin/getTransactionRecord?period=All`);
          if (resp?.data?.data) {
            collected = resp.data.data;
          }
        } else {
          // 2) Fund Provider: get own transactions by provider id (as used in SalesDetails)
          if (currentId) {
            try {
              const respById = await ApiCall.SECURE_API.get(`/admin/getDataUnitsSoldByProvider/${currentId}?period=All`);
              // Shape: { providerName, data: [...] }
              if (respById?.data?.data) {
                collected = respById.data.data;
              }
            } catch (e) {}
          }

          // 3) Fallback: provider self endpoint with/without institutionName
          if (!collected || collected.length === 0) {
            try {
              let resp = await ApiCall.SECURE_API.get(`/provider/getDataUnitsSoldByProvider?market=&isnstrument=&institutionName=${encodeURIComponent(currentProviderName || '')}&period=`);
              if (!(resp?.data?.success)) throw new Error('unsuccessful');
              collected = resp?.data?.data || [];

              if (!collected || collected.length === 0) {
                resp = await ApiCall.SECURE_API.get(`/provider/getDataUnitsSoldByProvider?market=&isnstrument=&period=`);
                if (resp?.data?.success) {
                  collected = resp.data.data || [];
                }
              }
            } catch (e) {}
          }
        }

        // Map collected records (if any) to table rows
        if (collected && collected.length) {
          const transformed = collected.map((item, index) => {
            const createdAt = item.createdAt || item.date || null;
            const instrument = item.instrument || item.type || 'Fund';
            const unitName = item.name || item.dataUnitName || item.unitName || `Data Unit ${index + 1}`;
            const unitsSold = item.units || item.quantity || item.totalUnitsSold || item.count || 0; // tickets

            const priceEth = item.priceEth || item.price || item.amount || item.ticketPriceEth;
            const priceUsd = item.priceUsd || item.usd || item.ticketPriceUsd;
            
            const grossEth = item.totalGrossAmountEth || item.totalRevenueEth || item.totalRevenue || item.revenue || 0;
            const grossUsd = item.totalGrossAmountUsd || item.revenueUsd || item.totalRevenueUsd;

            const netEth = item.totalNetAmountEth || item.netRevenueEth || item.netAmountEth || grossEth;
            const netUsd = item.totalNetAmountUsd || item.netRevenueUsd || item.netAmountUsd || grossUsd;

            return {
              createdAt,
              instrument,
              unitName,
              priceEth,
              priceUsd,
              unitsSold,
              grossEth,
              grossUsd,
              netEth,
              netUsd,
              id: item.id || item.tokenizedId || item.dataunitId || item._id || `t-${index}`,
            };
          });
          setRows(transformed);
        } else {
          setRows([]);
        }
      } catch (error) {
        setError('Failed to fetch transactions.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDataTransactions();
  }, [reloadKey]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <CircularProgress />
        <Typography style={{ marginLeft: '16px' }}>Loading data transactions...</Typography>
      </div>
    );
  }

  // Always render headings; show placeholders if empty
  return (
    <TableContainer style={{borderRadius:9}} component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="fund-transactions">
        <TableHead style={{backgroundColor:"Black"}}>
          <TableRow>
            <TableCell align="center" style={{color:"white"}}>Created At</TableCell>
            <TableCell align="center" style={{color:"white"}}>Units Sold</TableCell>
            <TableCell align="center" style={{color:"white"}}>Instrument</TableCell>
            <TableCell align="center" style={{color:"white"}}>Fund & Level</TableCell>
            <TableCell align="center" style={{color:"white"}}>Ticket Price</TableCell>
            <TableCell align="center" style={{color:"white"}}>New: Ticket Sold</TableCell>
            <TableCell align="center" style={{color:"white"}}>Gross Investment</TableCell>
            <TableCell align="center" style={{color:"white"}}>Net Investment</TableCell>
            <TableCell align="center" style={{color:"white"}}>Transaction</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(!rows || rows.length === 0) ? (
            <TableRow>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">-</TableCell>
            </TableRow>
          ) : (
          rows.map((row, idx) => (
            <TableRow key={row.id || idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
              <TableCell align="center">
                {row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}
              </TableCell>
              <TableCell align="center">{row.unitsSold ?? '-'}</TableCell>
              <TableCell align="center">{row.instrument ?? '-'}</TableCell>
              <TableCell align="center">{row.unitName ?? '-'}</TableCell>
              <TableCell align="center">
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
                  <span style={{ fontWeight:600 }}>{formatEth(row.priceEth)}</span>
                  <span style={{ color:'gray' }}>{formatUsd(row.priceUsd)}</span>
                </div>
              </TableCell>
              <TableCell align="center">{row.unitsSold ?? '-'}</TableCell>
              <TableCell align="center">
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
                  <span style={{ fontWeight:600 }}>{formatEth(row.grossEth)}</span>
                  <span style={{ color:'gray' }}>{formatUsd(row.grossUsd)}</span>
                </div>
              </TableCell>
              <TableCell align="center">
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start' }}>
                  <span style={{ fontWeight:600 }}>{formatEth(row.netEth)}</span>
                  <span style={{ color:'gray' }}>{formatUsd(row.netUsd)}</span>
                </div>
              </TableCell>
              <TableCell align="center">
                <Link to={`/admin-panel/FundSubscriberTransactions/${row.id}`}>View Details</Link>
              </TableCell>
            </TableRow>
          ))) }
        </TableBody>
      </Table>
    </TableContainer>
  );
}